import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/admin/PageHeader";
import { RequirePermission } from "@/components/admin/RequirePermission";
import { logAction } from "@/lib/admin/audit/log";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { adminCmsPageQuery } from "@/lib/cms/queries";
import type { CmsSectionBlock } from "@/lib/admin/cms/store";
import {
  deleteVideo,
  listAllVideos,
  listStoredVideoFiles,
  reorderVideos,
  saveVideo,
  signStoredVideo,
  uploadGalleryFileWithProgress,
  validateGalleryFile,
  type StoredVideoFile,
  type VideoGalleryItem,
  type VideoSource,
} from "@/lib/video-gallery/store";

export const Route = createFileRoute("/admin/video-gallery")({
  head: () => ({
    meta: [
      { title: "Sales Videos · CB16 Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: VideoGalleryPage,
});

const SOURCES: VideoSource[] = [
  "upload", "instagram", "youtube", "review", "influencer", "celebrity", "campaign",
];

type ProductOption = { id: string; name: string; slug: string };

function VideoGalleryPage() {
  return (
    <RequirePermission perm="cms.write">
      <VideoGalleryManager />
    </RequirePermission>
  );
}

function VideoGalleryManager() {
  const { user } = useAuth();
  const [rows, setRows] = useState<VideoGalleryItem[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<VideoGalleryItem | "new" | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listAllVideos());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    void supabase
      .from("products")
      .select("id,name,slug")
      .eq("is_active", true)
      .order("name")
      .then(({ data }) => setProducts((data ?? []) as ProductOption[]));
  }, [refresh]);

  async function toggleActive(item: VideoGalleryItem) {
    setBusy(item.id);
    try {
      await saveVideo({
        id: item.id,
        title: item.title,
        subtitle: item.subtitle,
        videoUrl: item.videoUrl,
        thumbnailUrl: item.thumbnailUrl,
        productId: item.productId,
        source: item.source,
        sortOrder: item.sortOrder,
        isActive: !item.isActive,
      });
      logAction({
        actorId: user?.id,
        actorEmail: user?.email ?? undefined,
        entity: "content",
        entityId: item.id,
        action: "update",
        summary: `${item.isActive ? "Disabled" : "Enabled"} gallery video ${item.title}`,
      });
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function move(index: number, dir: -1 | 1) {
    const next = [...rows];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setRows(next);
    await reorderVideos(next.map((r, i) => ({ id: r.id, sortOrder: i })));
    await refresh();
  }

  async function remove(item: VideoGalleryItem) {
    if (!window.confirm(`Delete “${item.title}”? This cannot be undone.`)) return;
    setBusy(item.id);
    try {
      await deleteVideo(item.id);
      logAction({
        actorId: user?.id,
        actorEmail: user?.email ?? undefined,
        entity: "content",
        entityId: item.id,
        action: "delete",
        summary: `Deleted gallery video ${item.title}`,
      });
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Sales Videos"
        subtitle="Fragrance videos for the homepage “Experience the Fragrance” section"
        action={
          <button
            type="button"
            onClick={() => setEditing("new")}
            className="border border-black bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white hover:border-[#C6A15B] hover:bg-[#C6A15B]"
          >
            Add video
          </button>
        }
      />

      <HomepageVideosPanel />

      <div className="flex flex-col gap-1">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-black">Video library</h2>
        <p className="text-xs text-black/50">
          Videos saved here can be picked for the homepage from CMS Pages → Home → Fragrance Films.
          New videos start as Inactive and are never shown to customers until you activate them and add them to the homepage.
        </p>
      </div>

      {error && (
        <p className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
      )}

      {loading ? (
        <p className="text-sm text-black/50">Loading videos…</p>
      ) : rows.length === 0 ? (
        <p className="border border-black/10 bg-white px-6 py-10 text-center text-sm text-black/50">
          No videos yet. Add one to activate the Shop by Video section.
        </p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((item, i) => (
            <div key={item.id} className="flex gap-4 border border-black/10 bg-white p-4">
              {item.videoUrl ? (
                <video
                  src={item.videoUrl}
                  poster={item.thumbnailUrl ?? undefined}
                  muted
                  playsInline
                  controls
                  preload="none"
                  className="h-32 w-20 shrink-0 rounded-md bg-black/5 object-cover"
                />
              ) : (
                <div className="flex h-32 w-20 shrink-0 items-center justify-center rounded-md border border-dashed border-[#C6A15B]/50 bg-[#faf7f2] px-1 text-center text-[9px] font-semibold uppercase tracking-widest text-[#C6A15B]">
                  Placeholder
                </div>
              )}
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p className="truncate text-sm font-semibold text-black">{item.title}</p>
                <p className="truncate text-xs text-black/50">
                  {item.product?.name ?? "No product linked"}
                </p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-black/40">
                  {item.source} · #{item.sortOrder}
                  {!item.videoUrl && " · awaiting upload"}
                </p>
                <span
                  className={
                    "mt-1 w-fit px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest " +
                    (item.isActive
                      ? "bg-green-50 text-green-700"
                      : "bg-black/5 text-black/50")
                  }
                >
                  {item.isActive ? "Active" : "Inactive"}
                </span>
                <div className="mt-auto flex flex-wrap gap-2 pt-2 text-[10px] font-semibold uppercase tracking-widest">
                  <button type="button" onClick={() => setEditing(item)} className="border border-black/15 px-2 py-1 hover:border-[#C6A15B]">Edit</button>
                  <button type="button" disabled={busy === item.id} onClick={() => void toggleActive(item)} className="border border-black/15 px-2 py-1 hover:border-[#C6A15B]">
                    {item.isActive ? "Deactivate" : "Activate"}
                  </button>
                  <button type="button" aria-label="Move up" onClick={() => void move(i, -1)} className="border border-black/15 px-2 py-1 hover:border-[#C6A15B]">↑</button>
                  <button type="button" aria-label="Move down" onClick={() => void move(i, 1)} className="border border-black/15 px-2 py-1 hover:border-[#C6A15B]">↓</button>
                  <button type="button" disabled={busy === item.id} onClick={() => void remove(item)} className="border border-red-200 px-2 py-1 text-red-600 hover:border-red-400">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <VideoEditor
          item={editing === "new" ? null : editing}
          products={products}
          nextOrder={rows.length}
          onClose={() => setEditing(null)}
          onSaved={async (summary, id) => {
            logAction({
              actorId: user?.id,
              actorEmail: user?.email ?? undefined,
              entity: "content",
              entityId: id,
              action: editing === "new" ? "create" : "update",
              summary,
            });
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

/**
 * Read-only view of the cards the homepage actually shows (Homepage →
 * Fragrance Films in the CMS). Editing stays in the CMS page builder so the
 * existing Save / Publish workflow is reused unchanged.
 */
function HomepageVideosPanel() {
  const { data: page, isLoading, error } = useQuery(adminCmsPageQuery("home"));
  const [productNames, setProductNames] = useState<Record<string, string>>({});
  const [galleryTitles, setGalleryTitles] = useState<Record<string, { title: string; url: string; thumb: string | null }>>({});

  const section = page?.blocks.find(
    (b): b is CmsSectionBlock => b.kind === "section" && (b as CmsSectionBlock).key === "films",
  );
  const cards = useMemo(
    () => (Array.isArray(section?.data?.cards) ? (section!.data.cards as Array<Record<string, string>>) : []),
    [section],
  );
  const limitRaw = typeof section?.data?.limit === "string" ? Number(section.data.limit) : NaN;
  const limit = Number.isFinite(limitRaw) && limitRaw > 0 ? limitRaw : 5;

  useEffect(() => {
    const pids = [...new Set(cards.map((c) => c.productId).filter(Boolean))];
    const gids = [...new Set(cards.map((c) => (c.video?.startsWith("gallery:") ? c.video.slice(8) : "")).filter(Boolean))];
    if (pids.length) {
      void supabase.from("products").select("id,name").in("id", pids).then(({ data }) => {
        setProductNames(Object.fromEntries((data ?? []).map((p) => [p.id, p.name])));
      });
    }
    if (gids.length) {
      void supabase.from("video_gallery").select("id,title,video_url,thumbnail_url").in("id", gids).then(({ data }) => {
        setGalleryTitles(Object.fromEntries((data ?? []).map((g) => [g.id, { title: g.title, url: g.video_url, thumb: g.thumbnail_url }])));
      });
    }
  }, [cards]);

  const visibleCount = cards.filter((c) => (c.hidden ?? "") !== "yes" && (c.video || c.productId)).length;
  const shown = Math.min(visibleCount, limit);

  return (
    <section className="border border-[#C6A15B]/40 bg-[#faf7f2] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-black">On the homepage now</h2>
          <p className="mt-1 text-xs text-black/60">
            {isLoading
              ? "Loading…"
              : cards.length === 0
                ? "No homepage cards are set, so the homepage shows the active videos from the library below."
                : `${cards.length} card${cards.length === 1 ? "" : "s"} set · ${visibleCount} published · ${shown} shown (display limit ${limit})`}
          </p>
        </div>
        <Link
          to="/admin/cms/$slug"
          params={{ slug: "home" }}
          className="border border-black bg-black px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-white hover:border-[#C6A15B] hover:bg-[#C6A15B]"
        >
          Edit homepage videos
        </Link>
      </div>
      {error && <p className="mt-3 text-sm text-red-700">{(error as Error).message}</p>}
      {cards.length > 0 && (
        <ol className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {cards.map((c, i) => {
            const gid = c.video?.startsWith("gallery:") ? c.video.slice(8) : "";
            const g = gid ? galleryTitles[gid] : undefined;
            const url = g?.url ?? (c.video && !gid ? c.video : "");
            const hidden = (c.hidden ?? "") === "yes";
            return (
              <li key={i} className="flex gap-3 border border-black/10 bg-white p-3">
                {url ? (
                  <video src={url} poster={c.thumbnail || g?.thumb || undefined} muted playsInline controls preload="none" className="h-24 w-14 shrink-0 rounded bg-black/5 object-cover" />
                ) : (
                  <div className="h-24 w-14 shrink-0 rounded border border-dashed border-black/15" />
                )}
                <div className="min-w-0 text-xs">
                  <p className="text-[10px] uppercase tracking-widest text-black/40">#{i + 1}</p>
                  <p className="truncate font-semibold text-black">
                    {c.overrideTitle || (c.productId ? productNames[c.productId] : "") || c.cardTitle || g?.title || "Untitled"}
                  </p>
                  <p className="truncate text-black/50">{gid ? `Library: ${g?.title ?? "…"}` : url ? "Uploaded video" : "No video"}</p>
                  <span className={"mt-1 inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest " + (hidden ? "bg-black/5 text-black/50" : "bg-green-50 text-green-700")}>
                    {hidden ? "Hidden" : "Published"}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

function VideoEditor({
  item, products, nextOrder, onClose, onSaved,
}: {
  item: VideoGalleryItem | null;
  products: ProductOption[];
  nextOrder: number;
  onClose: () => void;
  onSaved: (summary: string, id?: string) => void | Promise<void>;
}) {
  const [title, setTitle] = useState(item?.title ?? "");
  const [subtitle, setSubtitle] = useState(item?.subtitle ?? "");
  const [videoUrl, setVideoUrl] = useState(item?.videoUrl ?? "");
  const [thumbnailUrl, setThumbnailUrl] = useState(item?.thumbnailUrl ?? "");
  const [productId, setProductId] = useState(item?.productId ?? "");
  const [source, setSource] = useState<VideoSource>(item?.source ?? "upload");
  const [sortOrder, setSortOrder] = useState(item?.sortOrder ?? nextOrder);
  // New videos start inactive so nothing reaches customers until an admin activates it.
  const [isActive, setIsActive] = useState(item?.isActive ?? false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<"video" | "thumb" | null>(null);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [stored, setStored] = useState<StoredVideoFile[] | null>(null);
  const [storedErr, setStoredErr] = useState<string | null>(null);

  async function loadStored() {
    setStoredErr(null);
    try {
      setStored(await listStoredVideoFiles());
    } catch (e) {
      setStoredErr((e as Error).message);
    }
  }

  async function pickStored(path: string) {
    if (!path) return;
    setErr(null);
    try {
      setVideoUrl(await signStoredVideo(path));
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function handleUpload(file: File, kind: "video" | "thumb") {
    const problem = validateGalleryFile(file, kind);
    if (problem) {
      setErr(problem);
      return;
    }
    setUploading(kind);
    setProgress(0);
    setErr(null);
    try {
      const url = await uploadGalleryFileWithProgress(file, kind === "video" ? "videos" : "thumbnails", setProgress);
      if (kind === "video") setVideoUrl(url);
      else setThumbnailUrl(url);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setUploading(null);
    }
  }

  async function submit() {
    if (!title.trim()) {
      setErr("Please enter a title for this video.");
      return;
    }
    if (videoUrl.trim() && !/^(https?:\/\/|\/)/i.test(videoUrl.trim())) {
      setErr("The video link must start with https:// — or upload a file instead.");
      return;
    }
    if (isActive && !videoUrl.trim()) {
      setErr("Add a video before activating it.");
      return;
    }
    if (!Number.isFinite(sortOrder) || sortOrder < 0) {
      setErr("Display order must be 0 or higher.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await saveVideo({
        id: item?.id,
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        videoUrl: videoUrl.trim(),
        thumbnailUrl: thumbnailUrl.trim() || null,
        productId: productId || null,
        source,
        sortOrder,
        isActive,
      });
      await onSaved(`${item ? "Updated" : "Created"} gallery video ${title.trim()}`, item?.id);
    } catch (e) {
      setErr((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label={item ? "Edit video" : "Add video"} onClick={onClose}>
      <div className="max-h-[88vh] w-full max-w-lg overflow-y-auto bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-semibold uppercase tracking-[0.24em] text-black">
          {item ? "Edit video" : "Add video"}
        </h3>
        {err && <p role="alert" className="mt-3 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
        <div className="mt-4 grid gap-3 text-sm">
          <Field label="Tagline / title">
            <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="A warm amber, worn all day" />
          </Field>
          <Field label="Section subtitle (first video only)">
            <input className={inputCls} value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
          </Field>
          <Field label="Video — MP4, MOV or WebM, up to 50 MB">
            <div className="flex items-center gap-2">
              <input className={inputCls} value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://…" />
              <label className="shrink-0 cursor-pointer border border-black/15 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest hover:border-[#C6A15B]">
                {uploading === "video" ? `${progress}%` : "Upload"}
                <input type="file" aria-label="Upload video file" accept="video/mp4,video/quicktime,video/webm" className="hidden" disabled={!!uploading} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void handleUpload(f, "video"); }} />
              </label>
            </div>
          </Field>
          <div className="grid gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-black/50">Or choose an already-uploaded video</span>
            {stored === null ? (
              <button type="button" onClick={() => void loadStored()} className="w-fit border border-black/15 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest hover:border-[#C6A15B]">
                Show uploaded videos
              </button>
            ) : (
              <select aria-label="Already-uploaded videos" className={inputCls} defaultValue="" onChange={(e) => void pickStored(e.target.value)}>
                <option value="">— {stored.length} uploaded video{stored.length === 1 ? "" : "s"} —</option>
                {stored.map((f) => (
                  <option key={f.path} value={f.path}>
                    {f.name}{f.createdAt ? ` · ${new Date(f.createdAt).toLocaleDateString("en-IN")}` : ""}{f.sizeBytes ? ` · ${(f.sizeBytes / 1024 / 1024).toFixed(1)} MB` : ""}
                  </option>
                ))}
              </select>
            )}
            {storedErr && <p className="text-[11px] text-red-700">{storedErr}</p>}
          </div>
          {uploading && (
            <div className="grid gap-1" aria-live="polite">
              <div className="h-1.5 w-full overflow-hidden bg-black/10" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Upload progress">
                <div className="h-full bg-[#C6A15B] transition-[width]" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-[11px] text-black/50">Uploading {uploading === "video" ? "video" : "thumbnail"}… {progress}%</span>
            </div>
          )}
          {videoUrl.trim() && (
            <div className="grid gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-black/50">Preview</span>
              <video
                key={videoUrl}
                src={videoUrl}
                poster={thumbnailUrl || undefined}
                controls
                muted
                playsInline
                preload="metadata"
                className="h-56 w-32 rounded-md border border-black/10 bg-black object-cover"
                onError={() => setErr("This video could not be loaded for preview. Check the link or upload it again.")}
              />
            </div>
          )}
          <Field label="Thumbnail (poster) — JPG, PNG or WebP, up to 10 MB">
            <div className="flex items-center gap-2">
              <input className={inputCls} value={thumbnailUrl} onChange={(e) => setThumbnailUrl(e.target.value)} placeholder="https://…" />
              <label className="shrink-0 cursor-pointer border border-black/15 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest hover:border-[#C6A15B]">
                {uploading === "thumb" ? `${progress}%` : "Upload"}
                <input type="file" aria-label="Upload thumbnail image" accept="image/jpeg,image/png,image/webp,image/avif" className="hidden" disabled={!!uploading} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void handleUpload(f, "thumb"); }} />
              </label>
            </div>
            {thumbnailUrl.trim() && (
              <img src={thumbnailUrl} alt="Thumbnail preview" className="mt-1 h-24 w-16 rounded border border-black/10 object-cover" />
            )}
          </Field>
          <Field label="Related product">
            <select className={inputCls} value={productId} onChange={(e) => setProductId(e.target.value)}>
              <option value="">— None —</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Source">
              <select className={inputCls} value={source} onChange={(e) => setSource(e.target.value as VideoSource)}>
                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Display order">
              <input type="number" min={0} className={inputCls} value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active (available to the storefront)
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="border border-black/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] hover:border-[#C6A15B]">Cancel</button>
          <button type="button" disabled={saving || !!uploading} onClick={() => void submit()} className="border border-black bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white hover:border-[#C6A15B] hover:bg-[#C6A15B] disabled:opacity-50">
            {saving ? "Saving…" : "Save video"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#C6A15B]";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-black/50">{label}</span>
      {children}
    </label>
  );
}
